/**
 * Run sheet rows, split into the piles they are printed as.
 *
 * One group per thana, in the order the rows arrive — which is already thana
 * order, because the server sorts on it. Grouping here rather than asking the
 * server for a nested shape keeps the JSON one flat list that the area picker
 * can count and the print view can lay out, and keeps this arithmetic testable
 * without touching a component.
 */

/** The label a row with no thana is filed under. Matches the server's placeholder. */
export const NO_AREA = '—';

/**
 * @param  rows      run sheet rows, as `/orders/runSheet/preview` returns them
 * @param  areaIds   thana ids to keep; empty/undefined keeps every area
 * @return [{ thana, thanaId, rows }] in printing order
 */
export const runSheetGroups = (rows = [], areaIds = []) => {
  // A Set of the chosen ids, or null for "everything". Ids arrive from a form
  // control, so they can be strings — compared as strings on both sides rather
  // than trusting either to be a number.
  const wanted = areaIds?.length ? new Set(areaIds.map(String)) : null;

  const groups = new Map();

  for (const row of rows) {
    // A row with no thana has no id to pick it by, so it can only ever be in
    // the unfiltered sheet — never silently in a filtered one.
    if (wanted && !wanted.has(String(row.thanaId))) continue;

    const key = String(row.thanaId ?? NO_AREA);
    if (!groups.has(key)) {
      groups.set(key, { thana: row.thana || NO_AREA, thanaId: row.thanaId ?? null, rows: [] });
    }
    groups.get(key).rows.push(row);
  }

  return [...groups.values()];
};

/** How many rows a selection would print. What the buttons count. */
export const runSheetCount = (rows, areaIds) =>
  runSheetGroups(rows, areaIds).reduce((n, group) => n + group.rows.length, 0);
